// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React from 'react'
import { CloseOutlined } from '@ant-design/icons'
import { FilterItem } from 'components/camera-directory/FilterItem'
import { Checkbox, Button, Tooltip } from 'antd'
import { IDirectoryFilter } from 'models/directory'
import { OnlineStatus, DeviceStatus, TaskType, BatteryLevel, DataUsageLevel } from 'constants/device'
import {  deviceFilter } from "./CameraFilters";

export interface IDirectoryFilterProps {
  firmwareVersions: string[]
  onClose: () => void
  values: IDirectoryFilter
  onChange: (values: Partial<IDirectoryFilter>) => void
}

export class DirectoryFilter extends React.Component<IDirectoryFilterProps> {
  private handleChange = (key, value) => {
    this.props.onChange({ [key]: value})
  }

  render () {
    const { firmwareVersions, onClose, values } = this.props
    return (
      <div className='directory-filter'>
        <div className='title'>
          Filter
          <Button type='text' size='small' onClick={onClose}>
            <CloseOutlined />
          </Button>
        </div>
        <FilterItem
          defaultCollapsed={false}
          title='Test Mode'
          // titleExtra={<QuestionCircleOutlined />}
          className='filter-item'
        >
          <Checkbox
            className='filter-item-checkbox'
            checked={values.testMode}
            onChange={e => this.handleChange('testMode', e.target.checked)}
          >
            Only show cameras in test mode
          </Checkbox>
        </FilterItem>
        <FilterItem defaultCollapsed={false} title='Current Status' className='filter-item'>
          <Checkbox.Group value={values.onlineStatus} onChange={v => this.handleChange('onlineStatus', v)}>
            <div>
              <Checkbox className='filter-item-checkbox' value={OnlineStatus.Online}>
                Online
              </Checkbox>
            </div>
            <div>
              <Checkbox className='filter-item-checkbox' value={OnlineStatus.Offline}>
                Offline
              </Checkbox>
            </div>
          </Checkbox.Group>
        </FilterItem>

        <FilterItem defaultCollapsed={false} title={deviceFilter.title} className='filter-item'>
          <Checkbox.Group value={values[deviceFilter.key]} onChange={v => this.handleChange(deviceFilter.key, v)}>
            {deviceFilter.sections.map((section,index)=><div key={index}>
              <Checkbox className='filter-item-checkbox' value={section.value}>
                {section.name}
              </Checkbox>
            </div>)}
          </Checkbox.Group>
        </FilterItem>


        <FilterItem defaultCollapsed={false} title='Health Track' className='filter-item'>
          <Checkbox.Group value={values.status} onChange={v => this.handleChange('status', v)}>
            <div>
              <Checkbox className='filter-item-checkbox' value={DeviceStatus.Healthy}>
                Healthy
              </Checkbox>
            </div>
            <div>
              <Checkbox className='filter-item-checkbox' value={DeviceStatus.Issue}>
                Issued
              </Checkbox>
            </div>
            {/* <div>
              <Checkbox className='filter-item-checkbox' value={DeviceStatus.Damaged}>
                Damaged
              </Checkbox>
            </div> */}
          </Checkbox.Group>
        </FilterItem>
        <FilterItem defaultCollapsed={false} title='Task Type' className='filter-item'>
          <Checkbox.Group value={values.taskType} onChange={v => this.handleChange('taskType', v)}>
            <div>
              <Checkbox className='filter-item-checkbox' value={TaskType.Passive}>
                Passive
              </Checkbox>
            </div>
            <div>
              <Checkbox className='filter-item-checkbox' value={TaskType.Active}>
                Active
              </Checkbox>
            </div>
          </Checkbox.Group>
        </FilterItem>
        {/* <Index defaultCollapsed={false} title='Camera Type' className='filter-item'>
          <Checkbox.Group value={values.deviceType} onChange={v => this.handleChange('deviceType', v)}>
            <div>
              <Checkbox className='filter-item-checkbox' value={DeviceType.WorldCam}>
                World Cam
              </Checkbox>
            </div>
          </Checkbox.Group>
        </Index> */}
        <FilterItem defaultCollapsed={true} title='Firmware Version' className='filter-item'>
          <Checkbox.Group value={values.firmwareVersion} onChange={v => this.handleChange('firmwareVersion', v)}>
            {firmwareVersions.map(d => (
              <div key={d}>
                <Tooltip title={d}>
                  <Checkbox className='filter-item-checkbox' value={d}>
                    {d}
                  </Checkbox>
                </Tooltip>
              </div>
            ))}
            <div>
              <Checkbox className='filter-item-checkbox' value='unknown'>
                UnKnown
              </Checkbox>
            </div>
          </Checkbox.Group>
        </FilterItem>
        <FilterItem defaultCollapsed={false} title='Battery Level' className='filter-item'>
          <Checkbox.Group value={values.batteryLevel} onChange={v => this.handleChange('batteryLevel', v)}>
            <div>
              <Checkbox className='filter-item-checkbox' value={BatteryLevel.GT50}>
                Above 50%
              </Checkbox>
            </div>
            <div>
              <Checkbox className='filter-item-checkbox' value={BatteryLevel.GT20_LT50}>
                20%-50%
              </Checkbox>
            </div>
            <div>
              <Checkbox className='filter-item-checkbox' value={BatteryLevel.LT20}>
                10%-20%
              </Checkbox>
            </div>
            <div>
              <Checkbox className='filter-item-checkbox' value={BatteryLevel.LT10}>
                Below 10%
              </Checkbox>
            </div>
            <div>
              <Checkbox className='filter-item-checkbox' value={BatteryLevel.UnKnown}>
                UnKnown
              </Checkbox>
            </div>
          </Checkbox.Group>
        </FilterItem>
        <FilterItem defaultCollapsed={false} title='Data Usage Status' className='filter-item'>
          <Checkbox.Group value={values.quotaUsageLevel} onChange={v => this.handleChange('quotaUsageLevel', v)}>
            <div>
              <Checkbox className='filter-item-checkbox' value={DataUsageLevel.Normal}>
                Normal
              </Checkbox>
            </div>
            <div>
              <Checkbox className='filter-item-checkbox' value={DataUsageLevel.BeyondLimit}>
                Within Alert Range
              </Checkbox>
            </div>
            <div>
              <Checkbox className='filter-item-checkbox' value={DataUsageLevel.BeyondHardLimit}>
                Hard Limit Reached
              </Checkbox>
            </div>
          </Checkbox.Group>
        </FilterItem>
      </div>
    )
  }
}
